import type { ServerProvider, ServerProviderInstanceCapabilities } from "@afar-research/contracts";

export type ProviderInstanceControl = keyof ServerProviderInstanceCapabilities["controls"];

export function isProviderReadyForThreadCapability(
  provider: ServerProvider,
  capability: keyof ServerProviderInstanceCapabilities["threadCreation"],
): boolean {
  return (
    provider.enabled &&
    provider.availability !== "unavailable" &&
    provider.status === "ready" &&
    provider.readiness === "ready" &&
    provider.instanceCapabilities?.threadCreation[capability] === true
  );
}

/**
 * Whether a provider that could become usable is still checking its readiness, as opposed to
 * one that has settled as broken, unauthenticated, disabled, or unavailable.
 */
export function isProviderStarting(provider: ServerProvider): boolean {
  return (
    provider.enabled &&
    provider.availability !== "unavailable" &&
    provider.readiness === "initializing"
  );
}

export interface ProviderReadinessDescription {
  readonly readyProviderCount: number;
  readonly message: string;
}

/**
 * Describes provider setup without controlling access to unrelated Environment operations.
 */
export function describeProviderReadiness(
  providers: ReadonlyArray<ServerProvider> | null,
): ProviderReadinessDescription {
  if (providers === null) {
    return {
      readyProviderCount: 0,
      message: "Checking Provider Instance readiness.",
    };
  }

  const readyProviderCount = providers.filter(
    (provider) => provider.enabled && provider.status === "ready" && provider.readiness === "ready",
  ).length;
  if (readyProviderCount > 0) {
    return {
      readyProviderCount,
      message:
        readyProviderCount === 1
          ? "1 Provider Instance is ready."
          : `${readyProviderCount} Provider Instances are ready.`,
    };
  }

  return {
    readyProviderCount: 0,
    message: "Setup required: no enabled Provider Instance is ready.",
  };
}

/** AFAR Project Creation opens the new project on a provider turn, so it needs an instance that can. */
export function isProviderReadyForProjectCreation(
  providers: ReadonlyArray<ServerProvider>,
  instanceId: ServerProvider["instanceId"],
): boolean {
  return providers.some(
    (provider) =>
      provider.instanceId === instanceId &&
      isProviderReadyForThreadCapability(provider, "projectDefinition"),
  );
}

export function providerSupportsControl(
  provider: ServerProvider,
  control: ProviderInstanceControl,
): boolean {
  return provider.instanceCapabilities?.controls[control] === true;
}
